import { z } from '@customerio/hydra-validation';
import { persistentAtomWithFallback } from '@/utils/persistent-atom';
import { isProductionDevHelperEnvironment } from './store';

// The wallet lives in the billing backend and has no write endpoint, so the simulator rewrites
// the wallet in this browser's `GET /usage` responses instead. Source: services `domains/wallet/meter.go`.
export const WALLET_METERS = [
  'email_sends',
  'webhook_sends',
  'sms_sends',
  'active_profiles',
  'stored_objects',
  'inbox_previews',
  'ai_credits',
  'agent_usage',
] as const;

export type WalletMeter = (typeof WALLET_METERS)[number];

const count = z.number().int().min(0);

const grantSchema = z.object({ amount: count, remaining: count });

// Email sends that grow with the day of the billing period, so the spend per day, and with it the
// runway, stays where the preset put it.
const runwaySchema = z.object({
  days: z.number().int().min(1),
  sendsPerDay: z.number().min(0),
  periodStart: z.number().int().min(1),
});

const walletSimulationSchema = z.object({
  accountId: z.number().int(),
  balanceDollars: z.number().min(0),
  totalSpentDollars: z.number().min(0),
  hasPurchase: z.boolean(),
  grants: z.partialRecord(z.enum(WALLET_METERS), grantSchema),
  // Usage figures from the same response; null keeps the real one. Sends set the runway, profiles the free limit.
  billableMessagesSent: count.nullable(),
  runway: runwaySchema.nullable(),
  monthlyActiveUsers: count.nullable(),
  storedProfiles: count.nullable(),
});

export type WalletSimulation = z.infer<typeof walletSimulationSchema>;
export type SimulatedRunway = z.infer<typeof runwaySchema>;

export function parseWalletSimulation(value: unknown) {
  return walletSimulationSchema.safeParse(value);
}

function decodeSimulation(raw: string): WalletSimulation | null {
  if (isProductionDevHelperEnvironment()) return null;
  try {
    const parsed = parseWalletSimulation(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

// No cross-tab listener: every API request reads this atom, and a storage listener would be added
// and removed around each read. Applying or clearing reloads the page, and other tabs read it on reload.
export const $walletSimulation = persistentAtomWithFallback<WalletSimulation | null>(
  'dev-helper-wallet-simulation',
  null,
  { encode: JSON.stringify, decode: decodeSimulation, listen: false },
);

export function setWalletSimulation(simulation: WalletSimulation | null) {
  $walletSimulation.set(simulation);
}

const SECONDS_PER_DAY = 86_400;

/** The sends that keep a runway on track today. Day 1 is the period's first day, as the app counts it. */
export function runwaySends(runway: SimulatedRunway, now = new Date()) {
  const dayNumber = Math.max(Math.floor((now.getTime() / 1000 - runway.periodStart) / SECONDS_PER_DAY) + 1, 1);
  return Math.ceil(runway.sendsPerDay * dayNumber);
}

// Far enough out that no simulated bucket expires mid-session.
const SIMULATED_ENDS_AT = '2099-01-01T00:00:00Z';

function toWallet(simulation: WalletSimulation, startsAt: string) {
  const balance = simulation.balanceDollars;
  const showDeposit = simulation.hasPurchase || balance > 0;
  const deposits = showDeposit
    ? [
        {
          id: -1,
          kind: simulation.hasPurchase ? 'purchase' : 'goodwill',
          amount: balance + simulation.totalSpentDollars,
          remaining: balance,
          starts_at: startsAt,
          ends_at: SIMULATED_ENDS_AT,
        },
      ]
    : [];
  const grants = Object.fromEntries(
    WALLET_METERS.flatMap((meter, index) => {
      const grant = simulation.grants[meter];
      if (grant == null) return [];
      const bucket = { id: -(index + 2), ...grant, starts_at: startsAt, ends_at: SIMULATED_ENDS_AT };
      return [[meter, [bucket]]];
    }),
  );

  return {
    balance,
    total_spent: simulation.totalSpentDollars,
    has_funded: simulation.hasPurchase,
    buckets: { deposits, grants },
  };
}

const usageResponseSchema = z.looseObject({
  account_usage: z.looseObject({ environment_usages: z.array(z.looseObject({})).nullish() }),
});

type UsageResponse = z.infer<typeof usageResponseSchema>;

function billableMessagesSent(simulation: WalletSimulation, now: Date) {
  if (simulation.runway != null) return runwaySends(simulation.runway, now);
  return simulation.billableMessagesSent;
}

// Stored profiles are read from the account total or, with an unbilled workspace, the sum over
// workspaces. Putting them all on the first workspace makes both read the simulated figure.
function storedProfileFields(usage: UsageResponse['account_usage'], storedProfiles: number) {
  const environments = usage.environment_usages?.map((environment, index) => ({
    ...environment,
    customers_total: index === 0 ? storedProfiles : 0,
  }));
  return { customers_total: storedProfiles, ...(environments != null && { environment_usages: environments }) };
}

/** Rewrites the wallet, and any usage the simulation sets, in a `/usage` or `/usage_v2` body. */
export function applyWalletSimulation(body: UsageResponse, simulation: WalletSimulation, now = new Date()) {
  const usage = body.account_usage;
  const sends = billableMessagesSent(simulation, now);
  return {
    ...body,
    account_usage: {
      ...usage,
      wallet: toWallet(simulation, now.toISOString()),
      ...(sends != null && { billable_messages_sent: sends }),
      ...(simulation.monthlyActiveUsers != null && { monthly_active_users: simulation.monthlyActiveUsers }),
      ...(simulation.storedProfiles != null && storedProfileFields(usage, simulation.storedProfiles)),
    },
  } satisfies UsageResponse;
}

const USAGE_PATH = /^\/v1\/accounts\/(\d+)\/usage(?:_v2)?$/;

/** The account a usage GET is for, or null for any other request. */
function usageAccountId(input: RequestInfo | URL, init: RequestInit | undefined): number | null {
  const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
  if (method.toUpperCase() !== 'GET') return null;
  const href = input instanceof Request ? input.url : String(input);
  const match = USAGE_PATH.exec(new URL(href, window.location.origin).pathname);
  return match == null ? null : Number(match[1]);
}

/** The usage body, or null when it is not JSON or not a usage response. */
async function readUsageResponse(response: Response): Promise<UsageResponse | null> {
  try {
    const parsed = usageResponseSchema.safeParse(await response.clone().json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Rewrites the wallet in the simulated account's usage responses and passes everything else through. */
export const walletSimulatingFetch: typeof globalThis.fetch = async (input, init) => {
  const response = await globalThis.fetch(input, init);
  const simulation = $walletSimulation.get();
  if (simulation == null || !response.ok || usageAccountId(input, init) !== simulation.accountId) return response;

  const usage = await readUsageResponse(response);
  if (usage == null) return response;
  return Response.json(applyWalletSimulation(usage, simulation), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
};

/** The fetch for `hydraUiApi`. Production builds never simulate, so they get the plain fetch. */
export const walletSimulatorFetch = isProductionDevHelperEnvironment() ? undefined : walletSimulatingFetch;
